class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null;
    this.busy = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.setHeaderCartIconAccessibility();
    this.watchExternalAdds();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // Abre el carrito cuando otra app (p. ej. el botón fijo) añade productos.
  watchExternalAdds() {
    if (window.__asCartFetchPatched) return;
    window.__asCartFetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
        const headers = (args[1] && args[1].headers) || {};
        const internal = headers['X-As-Cart'] || (headers.get && headers.get('X-As-Cart'));
        if (url && /\/cart\/add(\.js)?(\?|$)/.test(url) && !internal && response.ok) {
          const handledByTheme = args[1] && args[1].body instanceof FormData && args[1].body.get('sections');
          if (!handledByTheme) setTimeout(() => this.refresh(true), 50);
        }
      } catch (e) {}
      return response;
    };
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.startTimer();
    const panel = this.querySelector('.as-cart__panel');
    if (panel) setTimeout(() => panel.focus({ preventScroll: true }), 260);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    if (this.activeElement) this.activeElement.focus({ preventScroll: true });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  renderContents(parsedState, openAfter = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHtml) return;
      if (section.id === 'cart-drawer') {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const fresh = doc.querySelector('#CartDrawer');
        const current = this.querySelector('#CartDrawer');
        if (fresh && current) current.replaceWith(fresh);
      } else {
        const target = document.getElementById(section.id);
        if (target) target.innerHTML = this.getSectionInnerHTML(sectionHtml);
      }
    });
    if (openAfter) this.open();
    else this.renderTimer();
  }

  async request(url, body) {
    this.busy = true;
    this.classList.add('is-busy');
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-As-Cart': '1' },
        body: JSON.stringify({
          ...body,
          sections: this.getSectionsToRender().map((s) => s.id),
          sections_url: window.location.pathname,
        }),
      });
      const state = await res.json();
      if (state.sections) {
        this.renderContents(state, false);
      } else {
        await this.refresh(false);
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.busy = false;
      this.classList.remove('is-busy');
    }
  }

  async refresh(openAfter) {
    const res = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`, { headers: { 'X-As-Cart': '1' } });
    const sections = await res.json();
    this.renderContents({ sections }, openAfter);
  }

  changeLine(line, quantity) {
    return this.request(`${routes.cart_change_url}.js`, { line: Number(line), quantity: Math.max(0, quantity) });
  }

  onClick(event) {
    const target = event.target;
    if (target.closest('[data-as-cart-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (this.busy) return;
    const item = target.closest('.as-cart__item');
    if (!item) return;
    const input = item.querySelector('[data-as-cart-qty]');
    const qty = parseInt(input.value, 10) || 0;
    if (target.closest('[data-as-cart-remove]')) this.changeLine(item.dataset.line, 0);
    else if (target.closest('[data-as-cart-minus]')) this.changeLine(item.dataset.line, qty - 1);
    else if (target.closest('[data-as-cart-plus]')) this.changeLine(item.dataset.line, qty + 1);
  }

  onChange(event) {
    const target = event.target;
    if (target.matches('[data-as-cart-qty]')) {
      const item = target.closest('.as-cart__item');
      const value = parseInt(String(target.value).replace(/\D/g, ''), 10);
      if (!isNaN(value)) this.changeLine(item.dataset.line, value);
    }
  }

  startTimer() {
    const minutes = parseInt(this.dataset.timerMinutes, 10) || 10;
    let end = parseInt(sessionStorage.getItem('asCartTimerEnd') || '0', 10);
    if (!end) {
      end = Date.now() + minutes * 60 * 1000;
      try {
        sessionStorage.setItem('asCartTimerEnd', String(end));
      } catch (e) {}
    }
    this.timerEnd = end;
    this.renderTimer();
    clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => this.renderTimer(), 1000);
  }

  renderTimer() {
    if (!this.timerEnd) return;
    const left = Math.max(0, Math.round((this.timerEnd - Date.now()) / 1000));
    const text = String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');
    this.querySelectorAll('[data-as-cart-timer]').forEach((el) => (el.textContent = text));
    if (left === 0) clearInterval(this.timerInterval);
  }
}

customElements.define('cart-drawer', CartDrawer);
